import React, { useMemo, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  DatePicker,
} from '@finpay-development/shared-components';
import {
  Box,
  Grid,
  Paper,
  Typography,
  MenuItem,
} from '@mui/material';
import { AttachMoney as AttachMoneyIcon } from '@mui/icons-material';
import { Utils } from '../../shared/utils';
import { commonFields } from '../../shared/validation/schemas';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import { ClientStatusCardViewModel } from '../../shared/model/client-status-card';
import { showErrorStatus } from '../../security/state/user-slice';
import '../../estimates/tab-styling.scss';

interface NewPatientFormValues {
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  accountNumber: string;
  email: string;
  pfr: string;
  clientId: string;
  facilityId: string;
}

interface MockFacility {
  facilityId: number;
  facilityName: string;
}

const mockFacilities: MockFacility[] = [
  { facilityId: 1, facilityName: 'Main Medical Center' },
  { facilityId: 2, facilityName: 'Downtown Clinic' },
  { facilityId: 3, facilityName: 'Northside Hospital' },
  { facilityId: 4, facilityName: 'Westside Medical Facility' },
  { facilityId: 5, facilityName: 'Community Health Center' },
];

const validationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  dateOfBirth: Yup.date()
    .nullable()
    .required('Date of birth is required')
    .max(new Date(), 'Date of birth cannot be in the future'),
  accountNumber: Yup.string().optional(),
  email: Yup.string().email('Email Format is Invalid').optional(),
  clientId: commonFields.REQUIRED_DROPDOWN,
  facilityId: commonFields.REQUIRED_DROPDOWN,
});

function NewPatientView() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const shouldShowErrorMessage = useRef(true);

  const clients = useSelector((state: RootState) =>
    state.implementationContext.implementationSpecialistClient.filteredByIsActive
  ) as ClientStatusCardViewModel[];

  const initialValues: NewPatientFormValues = useMemo(() => ({
    firstName: '',
    lastName: '',
    dateOfBirth: null,
    accountNumber: '',
    email: '',
    pfr: '',
    clientId: '',
    facilityId: '',
  }), []);

  const formik = useFormik({
    initialValues,
    validate: async (values: NewPatientFormValues) => {
      const errors: any = {};
      let message = `Please resolve the following issues: `;
      try {
        await validationSchema.validate(values, { abortEarly: false });
      } catch (err: any) {
        err.inner.forEach((error: any) => {
          errors[error.path] = error.message;
          message += ` ${error.message};`;
        });

        if (shouldShowErrorMessage.current) {
          dispatch(showErrorStatus(message));
          shouldShowErrorMessage.current = false;
        }
      }
      return errors;
    },
    validationSchema,
    onSubmit: async (values) => {
      // Handle form submission
      console.log('Form submitted:', values);
    },
  });

  // Set first client when clients are loaded
  useEffect(() => {
    if (clients.length > 0 && !formik.values.clientId) {
      formik.setFieldValue('clientId', clients[0].clientId.toString(), false);
    }
  }, [clients]);

  // Set first facility when component loads
  useEffect(() => {
    if (mockFacilities.length > 0 && !formik.values.facilityId) {
      formik.setFieldValue('facilityId', mockFacilities[0].facilityId.toString(), false);
    }
  }, []);

  const handleTakePayment = async () => {
    shouldShowErrorMessage.current = true;
    const errors = await formik.validateForm();
    
    if (Object.keys(errors).length === 0) {
      // Always navigate to the hardcoded route
      navigate('/hub/patient/194180/ioc/300054/pay');
    }
  };

  const handleSetupPaymentProgram = async () => {
    shouldShowErrorMessage.current = true;
    const errors = await formik.validateForm();
    
    if (Object.keys(errors).length === 0) {
      // Always navigate to the hardcoded route
      navigate('/hub/patient/194180/ioc/300054/program');
    }
  };

  return (
    <main test-id="new-patient-view">
      <div className="content px-4 py-4">
        <Paper elevation={2} sx={{ p: 4, maxWidth: 900, mx: 'auto' }} test-id="new-patient-form-paper">
          <Typography variant="h5" component="h1" align="center" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Payment Hub
          </Typography>

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
                {/* Client Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 56,
                        paddingBottom: '16px',
                      },
                    }}
                  >
                    <TextField
                      select
                      error={formik.touched.clientId && !!formik.errors.clientId}
                      label="Client"
                      name="clientId"
                      value={formik.values.clientId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      helperText={formik.touched.clientId ? formik.errors.clientId : undefined}
                      className={`state-field ${formik.touched.clientId && Boolean(formik.errors.clientId) ? 'error-border' : 'required-fields'}`}
                      test-id="new-patient-client"
                    >
                      <MenuItem value="">
                        <em>Select Client</em>
                      </MenuItem>
                      {clients.map((client) => (
                        <MenuItem key={client.clientId} value={client.clientId.toString()}>
                          {client.clientName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Grid>

                {/* Facility Section */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      '& .MuiInputBase-root': {
                        height: 56,
                        paddingBottom: '16px',
                      },
                    }}
                  >
                    <TextField
                      select
                      error={formik.touched.facilityId && !!formik.errors.facilityId}
                      label="Facility"
                      name="facilityId"
                      value={formik.values.facilityId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      helperText={formik.touched.facilityId ? formik.errors.facilityId : undefined}
                      className={`state-field ${formik.touched.facilityId && Boolean(formik.errors.facilityId) ? 'error-border' : 'required-fields'}`}
                      test-id="new-patient-facility"
                    >
                      <MenuItem value="">
                        <em>Select Facility</em>
                      </MenuItem>
                      {mockFacilities.map((facility) => (
                        <MenuItem key={facility.facilityId} value={facility.facilityId.toString()}>
                          {facility.facilityName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Grid>

                {/* First Name and Last Name Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, width: '100%', boxSizing: 'border-box' }}>
                    <Box sx={{ flex: '1 1 0', minWidth: 0, '& .MuiInputBase-root': { height: 56 } }}>
                      <TextField
                        error={formik.touched.firstName && !!formik.errors.firstName}
                        label="First Name"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.firstName ? formik.errors.firstName : undefined}
                        className={`state-field ${formik.touched.firstName && Boolean(formik.errors.firstName) ? 'error-border' : 'required-fields'}`}
                        test-id="new-patient-first-name"
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 0', minWidth: 0, '& .MuiInputBase-root': { height: 56 } }}>
                      <TextField
                        error={formik.touched.lastName && !!formik.errors.lastName}
                        label="Last Name"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        helperText={formik.touched.lastName ? formik.errors.lastName : undefined}
                        className={`state-field ${formik.touched.lastName && Boolean(formik.errors.lastName) ? 'error-border' : 'required-fields'}`}
                        test-id="new-patient-last-name"
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* Date of Birth and Email Row */}
                <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, width: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ flex: '0 0 23.33%', minWidth: 0, '& .MuiInputBase-root': { height: 56 } }}>
                    <DatePicker
                      error={
                        formik.touched.dateOfBirth &&
                        !!formik.errors.dateOfBirth
                      }
                      label="Date of Birth"
                      value={formik.values.dateOfBirth}
                      onChange={(date: Date) => {
                        if (Utils.isValidDate(date)) {
                          formik.setFieldValue('dateOfBirth', date);
                        }
                      }}
                      maxDate={new Date()}
                      className={`date state-field ${formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth) ? 'error-border' : 'required-fields'}`}
                      data="new-patient-date-of-birth"
                    />
                    {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {formik.errors.dateOfBirth}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: '1 1 0', minWidth: 0, '& .MuiInputBase-root': { height: 56 } }}>
                    <TextField
                      error={formik.touched.email && !!formik.errors.email}
                      label="Email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      helperText={formik.touched.email ? formik.errors.email : undefined}
                      test-id="new-patient-email"
                    />
                  </Box>
                </Grid>

                {/* PFR and Account Number Row */}
                <Grid item xs={12} sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, width: '100%', boxSizing: 'border-box' }}>
                  <Box sx={{ flex: '0 0 23.33%', minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                    <TextField
                      error={formik.touched.pfr && !!formik.errors.pfr}
                      label="PFR"
                      name="pfr"
                      startAdornment={<AttachMoneyIcon />}
                      value={formik.values.pfr}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const rawValue = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = rawValue.split('.');
                        let cleanValue = parts[0];
                        if (parts.length > 1) {
                          cleanValue += '.' + parts[1].substring(0, 2);
                        }
                        formik.setFieldValue('pfr', cleanValue, false);
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        formik.handleBlur(e);
                        const value = parseFloat(e.target.value.replace(/,/g, ''));
                        if (!isNaN(value) && value >= 0) {
                          formik.setFieldValue('pfr', value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), false);
                        }
                      }}
                      type="text"
                      helperText={formik.touched.pfr ? formik.errors.pfr : undefined}
                      test-id="new-patient-pfr"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 0', minWidth: 0, '& .MuiInputBase-root': { height: 56 } }}>
                    <TextField
                      error={formik.touched.accountNumber && !!formik.errors.accountNumber}
                      label="Account Number"
                      name="accountNumber"
                      value={formik.values.accountNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      helperText={formik.touched.accountNumber ? formik.errors.accountNumber : undefined}
                      test-id="new-patient-account-number"
                    />
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ width: '100%' }}>
                      <Button
                        type="primary"
                        onClick={handleTakePayment}
                        test-id="new-patient-take-payment"
                        style={{ 
                          width: '100%', 
                          paddingTop: '1rem',
                          paddingBottom: '1rem',
                          fontSize: '2rem',
                          fontWeight: 600
                        }}
                      >
                        Take a Payment
                      </Button>
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <Button
                        type="primary"
                        onClick={handleSetupPaymentProgram}
                        test-id="new-patient-setup-payment-program"
                        style={{ 
                          width: '100%', 
                          paddingTop: '1rem',
                          paddingBottom: '1rem',
                          fontSize: '2rem',
                          fontWeight: 600
                        }}
                      >
                        Setup Payment Program
                      </Button>
                    </Box>
                  </Box>
                </Grid>
            </Grid>
          </form>
        </Paper>
      </div>
    </main>
  );
}

export default NewPatientView;

